import { useCallback, useEffect, useState } from 'react'
import {
  addBookToReaderList,
  createReaderList,
  getListsContainingBook,
  getReaderLists,
  removeBookFromReaderList
} from 'api/reader-lists'
import type { ReaderListSummary } from 'api/reader-lists'
import { useToast } from 'utils/hooks/use-toast/useToast'
import { useTranslation } from 'i18n'

export const useAddToList = ({ slug }: { slug: string }) => {
  const { t } = useTranslation()
  const { toastMessage } = useToast()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [lists, setLists] = useState<ReaderListSummary[]>([])
  const [checkedIds, setCheckedIds] = useState<number[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newListName, setNewListName] = useState('')

  useEffect(() => {
    if (!slug) return
    getListsContainingBook(slug)
      .then(response => setCheckedIds(response.data?.listIds ?? []))
      .catch(() => setCheckedIds([]))
  }, [slug])

  const openModal = useCallback(async () => {
    setIsModalOpen(true)
    setIsLoading(true)
    try {
      const [listsResponse, containingResponse] = await Promise.all([
        getReaderLists(),
        getListsContainingBook(slug)
      ])
      setLists(listsResponse.data?.data ?? [])
      setCheckedIds(containingResponse.data?.listIds ?? [])
    } catch (err) {
      setLists([])
    } finally {
      setIsLoading(false)
    }
  }, [slug])

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    setShowCreateForm(false)
    setNewListName('')
  }, [])

  const toggleList = useCallback(
    (listId: number) => {
      const wasChecked = checkedIds.includes(listId)
      setCheckedIds(current =>
        wasChecked ? current.filter(id => id !== listId) : [...current, listId]
      )

      const revert = () => {
        setCheckedIds(current =>
          wasChecked
            ? [...current, listId]
            : current.filter(id => id !== listId)
        )
        toastMessage({
          message: t('Não foi possível atualizar a lista'),
          type: 'error'
        })
      }

      if (wasChecked) {
        removeBookFromReaderList(listId, slug).catch(revert)
      } else {
        addBookToReaderList(listId, slug)
          .then(() => {
            toastMessage({
              message: t('Livro adicionado à lista'),
              textButton: {
                text: t('Ver lista'),
                onClick: () => {
                  window.location.href = `/app-v2/my-readings/lists/${listId}`
                }
              }
            })
          })
          .catch(revert)
      }
    },
    [checkedIds, slug, t, toastMessage]
  )

  const createList = useCallback(async () => {
    const name = newListName.trim()
    if (!name || isCreating) return
    setIsCreating(true)
    try {
      const response = await createReaderList(name)
      const listId = response.data?.id
      if (listId) {
        await addBookToReaderList(listId, slug)
        const listsResponse = await getReaderLists()
        setLists(listsResponse.data?.data ?? [])
        setCheckedIds(current => [...current, listId])
        toastMessage({
          message: t('Livro adicionado à lista'),
          textButton: {
            text: t('Ver lista'),
            onClick: () => {
              window.location.href = `/app-v2/my-readings/lists/${listId}`
            }
          }
        })
      }
      setNewListName('')
      setShowCreateForm(false)
    } catch (err) {
      toastMessage({
        message: t('Não foi possível criar a lista'),
        type: 'error'
      })
    } finally {
      setIsCreating(false)
    }
  }, [newListName, isCreating, slug, t, toastMessage])

  return {
    isInAnyList: checkedIds.length > 0,
    isModalOpen,
    lists,
    checkedIds,
    isLoading,
    isCreating,
    showCreateForm,
    newListName,
    setNewListName,
    setShowCreateForm,
    openModal,
    closeModal,
    toggleList,
    createList
  }
}
